/**
 * dog-details.js
 */

import React from 'react';
import axios from 'axios';
import { useTheme, Box, FormGroup, FormControlLabel, Switch, Grid, FormHelperText, Chip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Alert from '@mui/material/Alert';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addMonths, parseISO, startOfToday, endOfDay, addDays, isToday } from 'date-fns';
import { useForm, useWatch, FormProvider, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import Button from 'components/button';
import { EditorIconSection } from 'components/icon-section/icon-section';
import DrawerContent from 'components/menu-drawer/drawer-content';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DogDrawerMobileHeader from './dog-drawer-mobile-header';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TextField from 'components/form-inputs/text-field';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import CheckboxList from 'components/form-inputs/checkbox-list';


const useStyles = makeStyles(theme => ({
  dogType: {
    minWidth: theme.spacing(45),
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%'
    },
  },
}));


const DogDetails = function(props) {
  const {
    drawerState,
    dog: propsDog,
    userId,
    forceDefault,
    basePath,
    onSave
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const dog = React.useMemo(() => propsDog || {}, [propsDog]);
  const { enqueueSnackbar } = useSnackbar();
  const translation = 'dogDetails';
  const { t } = useTranslation(translation);
  const { t: gt } = useTranslation('general');
  const [ready, setReady] = React.useState(false);
  const [patrolTypes] = React.useState(Types.Patrol.Training.entries(gt));
  const [odorTypes] = React.useState(Types.OdorPrimary.entries(gt, [Types.OdorPrimary.PROOFING]));
  const title = 'Details';
  const formContext = useForm();
  const {
    handleSubmit,
    control,
    formState,
    getValues,
    reset,
    clearErrors,
  } = formContext;
  const dateRetired = useWatch({ control, name: 'dateRetired' });


  // init hook
  React.useEffect(() => {
    if (!dog) return;

    let dogFields = {
      name: '',
      breed: '',
      type: '',
      isDefault: forceDefault || false,
      dateStarted: addMonths(startOfToday(), -3),
      dateRetired: null,
      dateBirth: null,
      patrolTypes: [],
      detectionOdors: []
    };

    if (dog && dog.id) {
      dogFields = {...dogFields, ...dog};
    }

    reset(dogFields);
    setReady(true);

  }, [dog, forceDefault, reset]);


  // saves dog form when validation succeeds
  const onSubmit = async (data) => {
    try {
      drawerState.loading.content.set(true);
      const response = await axios({
        method: dog.id ? 'put' : 'post',
        url: '/dogs' + (dog.id ? '/' + dog.id : ''),
        data: userId ? { ...data, userId } : data, // include userId if we have it
        bypassErrorHandling: true
      });
      if (response.isSuccess) {
        const newDog = response.data;
        const updatedDog = {
          ...newDog,
          dateBirth: newDog.dateBirth? parseISO(newDog.dateBirth) : null,
          dateStarted: newDog.dateStarted? parseISO(newDog.dateStarted) : null,
          dateRetired: newDog.dateRetired? parseISO(newDog.dateRetired) : null
        };
        reset(updatedDog);
        onSave(updatedDog);
        enqueueSnackbar('Dog saved', { variant: 'success' });
        if (!dog.id) {
          setTimeout(() => history.replace(`/dogs/${newDog.id}/edit/details`));
        }
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      drawerState.loading.content.set(false);
    }
  };


  // validates at least one attribute is selected
  const validateAttributes = (message) => {
    const {patrolTypes, detectionOdors} = getValues();
    if (patrolTypes.length > 0 || detectionOdors.length > 0) {
      clearErrors('patrolTypes');
      clearErrors('detectionOdors');
      return true;
    }

    return message;
  }


  const cancelButton = (
    <Button upperCase={false} onClick={props.onClose}>Cancel</Button>
  );


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty}
      style={{ marginLeft: 16 }}
      onClick={() => handleSubmit(onSubmit)()}
      data-testid='save-dog-button'
    >
      Save
    </DrawerActionButton>
  );


  const mobileAppBar = (
    <DogDrawerMobileHeader
      title={title}
      onClose={props.onClose}
      saveButton={saveButton}
      basePath={basePath}
    />
  );


  return (
    <>
      {ready &&
      <DrawerContentBuilder
        mobileAppBar={{ content: mobileAppBar }}
        desktopHeader={{ title, actions: <>{cancelButton}{saveButton}</> }}
        stickyHeader
        children={(
          <FormProvider {...formContext}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DrawerContent>
                {/* general info section */}
                <EditorIconSection
                  iconName='dog-profile'
                  helpProps={{
                    translation,
                    section: 'general'
                  }}
                >
                  <Box>

                    {/* name */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('general.name.helpTitle')}
                      text={t('general.name.helpInfo')}
                    >
                      {focusHandlers =>
                        <Grid container spacing={1}>
                          <FormGridItem>
                            <TextField
                              name='name'
                              label={t('general.name.label')}
                              focusHandlers={focusHandlers}
                              rules={{
                                required: t('general.name.errMsgEmpty'),
                                validate: value => value.trim() !== '' || t('general.name.errMsgEmpty'),
                                maxLength: {
                                  value: 50,
                                  message: t('general.name.errMsgLength')
                                }
                              }}
                            />
                          </FormGridItem>
                          <FormGridItem className='flex-hc'>
                            <Chip
                              label={(dateRetired && !isToday(dateRetired)) ? 'Retired' : 'Active'}
                              size="small"
                              className={`${classes.chip} ${(dateRetired && !isToday(dateRetired)) ? 'chip-outdated chip-font-outdated' : 'chip-ok chip-font-ok'}`}
                              style={{ lineHeight: 1.2 }}
                            />
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>

                    {/* breed */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('general.breed.helpTitle')}
                      text={t('general.breed.helpInfo')}
                    >
                      {focusHandlers =>
                        <Grid container>
                          <FormGridItem>
                            <TextField
                              name='breed'
                              label={t('general.breed.label')}
                              focusHandlers={focusHandlers}
                              rules={{
                                required: t('general.breed.errMsgEmpty'),
                                validate: value => value.trim() !== '' || t('general.breed.errMsgEmpty'),
                                maxLength: {
                                  value: 50,
                                  message: t('general.breed.errMsgLength')
                                }
                              }}
                            />
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>

                    {/* purpose */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('general.purpose.helpTitle')}
                      text={t('general.purpose.helpInfo')}
                    >
                      {focusHandlers =>
                        <TextField
                          name='type'
                          label={t('general.purpose.label')}
                          focusHandlers={focusHandlers}
                          className={`${classes.dogType}`}
                          rules={{
                            required: t('general.purpose.errMsgEmpty'),
                            validate: value => value.trim() !== '' || t('general.purpose.errMsgEmpty'),
                            maxLength: {
                              value: 50,
                              message: t('general.purpose.errMsgLength')
                            }
                          }}
                        />}
                    </HelpTextItem>

                    {/* birth date */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('general.dob.helpTitle')}
                      text={t('general.dob.helpInfo')}
                    >
                      {focusHandlers =>
                        <Grid container>
                          <FormGridItem>
                            <DateTimePicker
                              variant='date'
                              name='dateBirth'
                              label={t('general.dob.label')}
                              focusHandlers={focusHandlers}
                              format="MM/dd/yyyy"
                              clearable
                              closeOnSelect={false}
                            />
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>

                    {/* default dog selector */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('general.defaultDog.helpTitle')}
                      text={t('general.defaultDog.helpInfo')}
                    >
                      <Controller
                        control={control}
                        name="isDefault"
                        render={({ field }) =>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="primary"
                                  checked={field.value}
                                  onChange={e => field.onChange(e.target.checked)}
                                  disabled={Boolean(dateRetired)}
                                />
                              }
                              label={`This is ${userId ? 'their' : 'my'} default dog`}
                            />
                          </FormGroup>}
                      />
                    </HelpTextItem>
                  </Box>
                </EditorIconSection>

                {/* stats section */}
                <EditorIconSection
                  iconName="stats"
                  helpProps={{
                    translation,
                    section: 'recordKeeping'
                  }}
                >
                  <Box>
                                         
                    {/* start date */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('recordKeeping.startDate.helpTitle')}
                      text={t('recordKeeping.startDate.helpInfo')}
                    >
                      {focusHandlers =>
                        <Grid container spacing={1}>
                          <FormGridItem>
                            <DateTimePicker
                              variant='date'
                              name='dateStarted'
                              label={t('recordKeeping.startDate.label')}
                              focusHandlers={focusHandlers}
                              format="MM/dd/yyyy"
                              closeOnSelect={false}
                              setValueAs={date => {
                                if (date) {
                                  date.setHours(0, 0, 0, 0); // set time to 12AM
                                  return date;
                                }
                                return null;
                              }}
                              rules={{ required: t('recordKeeping.startDate.errMsgEmpty') }}
                            />
                          </FormGridItem>
                          <FormGridItem className='flex-hc'>
                            <FormHelperText style={{ marginLeft: 10, lineHeight: 1.2 }}>
                              Choose carefully! You cannot <br/>add records before this date.
                            </FormHelperText>
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>

                    {/* end/retirement date */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('recordKeeping.endDate.helpTitle')}
                      text={t('recordKeeping.endDate.helpInfo')}
                    >
                      {focusHandlers =>
                        <Grid container>
                          <FormGridItem>
                            <DateTimePicker
                              variant='date'
                              name='dateRetired'
                              label={t('recordKeeping.endDate.label')}
                              focusHandlers={focusHandlers}
                              format="MM/dd/yyyy"
                              closeOnSelect={false}
                              clearable
                              maxDate={addDays(new Date(), 1)}
                              maxDateMessage={t('recordKeeping.endDate.errMsgMaxDate')}
                              setValueAs={date => {
                                if (date) {
                                  return endOfDay(date);
                                }
                                return null;
                              }}
                            />
                          </FormGridItem>
                          <FormGridItem className='flex-hc'>
                            <FormHelperText style={{ marginLeft: 10, lineHeight: 1.2 }}>
                              Leave this blank unless your <br/>dog is retired.
                            </FormHelperText>
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>
                  </Box>
                </EditorIconSection>

                {/* attributes section */}
                <EditorIconSection
                  iconName="attributes"
                  iconMarginTop={0}
                  helpProps={{
                    translation,
                    section: 'k9Attributes'
                  }}
                >
                  <div>
                    {Boolean(formState.errors?.patrolTypes) &&
                    <Alert severity="error">{formState.errors?.patrolTypes?.message}</Alert>}

                    {/* patrol types */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('k9Attributes.patrolTypes.helpTitle')}
                      text={t('k9Attributes.patrolTypes.helpInfo')}
                      style={{marginBottom: theme.spacing(1)}}
                    >
                      {focusHandlers =>
                        <CheckboxList
                          name='patrolTypes'
                          dataTestId="patroltype-checkboxes"
                          label={t('k9Attributes.patrolTypes.label')}
                          options={patrolTypes}
                          focusHandlers={focusHandlers}
                          rules={{
                            validate: () => validateAttributes(t(`k9Attributes.patrolTypes.errMsgEmpty`))
                          }}
                          helperText={t('k9Attributes.patrolTypes.fieldInfo')}
                          disableErrorMessage
                          getOptionChecked={(option, checkList) => checkList.includes(option.id)}
                          setOptionChecked={(option, checkList, isChecked) => isChecked
                            ? [...checkList, option.id]
                            : checkList.filter(i => i !== option.id)}
                        />}
                    </HelpTextItem>

                    {/* detection types */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('k9Attributes.detectionOdors.helpTitle')}
                      text={t('k9Attributes.detectionOdors.helpInfo')}
                      style={{marginBottom: theme.spacing(1)}}
                    >
                      {focusHandlers =>
                        <CheckboxList
                          name='detectionOdors'
                          dataTestId="odortype-checkboxes"
                          label={t('k9Attributes.detectionOdors.label')}
                          options={odorTypes}
                          focusHandlers={focusHandlers}
                          rules={{
                            validate: () => validateAttributes(t(`k9Attributes.detectionOdors.errMsgEmpty`))
                          }}
                          helperText={t('k9Attributes.detectionOdors.fieldInfo')}
                          disableErrorMessage
                          getOptionChecked={(option, checkList) => checkList.includes(option.id)}
                          setOptionChecked={(option, checkList, isChecked) => isChecked
                            ? [...checkList, option.id]
                            : checkList.filter(i => i !== option.id)}
                        />}
                    </HelpTextItem>
                  </div>
                </EditorIconSection>
              </DrawerContent>
            </form>
          </FormProvider>
        )}
      />}

      <ConfirmUnsavedEditor
        dirty={formState.isDirty}
        save={async () => handleSubmit(onSubmit)()}
      />
    </>
  );
}

export default DogDetails;
